import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, Search, HelpCircle, Home, Users, FileText, Shield, Copy, X, Filter, ArrowUpRight, ArrowLeft, Bot, ClipboardList } from 'lucide-react';
import { CreatePolicyModal } from './components/CreatePolicyModal';
import { CreditApplication } from './components/CreditApplication';
import { WonkaCreditApplication } from './components/WonkaCreditApplication';
import { InitechCreditApplication } from './components/InitechCreditApplication';
import { AdminDashboard } from './components/AdminDashboard';
import { AdvancedSearch } from './components/AdvancedSearch';
import { MonitorPage } from './components/MonitorPage';
import { ApplicationHub } from './components/ApplicationHub';
import { GlobalSearch } from './components/GlobalSearch';
export function App() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [policyCreated, setPolicyCreated] = useState(false);
  const [currentPage, setCurrentPage] = useState<'policies' | 'application' | 'admin' | 'search' | 'monitor' | 'applicationHub'>('monitor');
  const [showStartArrow, setShowStartArrow] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState<'dunder' | 'wonka' | 'initech'>('dunder');
  const handleOpenModal = () => {
    setIsModalOpen(true);
    setShowStartArrow(false);
  };
  const handleCloseModal = (created?: boolean) => {
    setIsModalOpen(false);
    if (created) {
      setPolicyCreated(true);
    }
  };
  const handleCompanyClick = (company: 'dunder' | 'wonka' | 'initech') => {
    setSelectedCompany(company);
    setCurrentPage('application');
  };
  return <div className="flex h-screen w-full bg-gray-50" data-id="element-0">
      {/* Sidebar */}
      <div className="w-16 bg-white border-r border-gray-200 flex flex-col items-center py-6 space-y-6 z-20" data-id="element-1">
        <button onClick={() => setCurrentPage('monitor')} className={`p-2 rounded-lg transition-colors ${currentPage === 'monitor' ? 'text-indigo-600 bg-indigo-50' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-2">
          <Home className="w-5 h-5" data-id="element-3" />
        </button>
        <button onClick={() => setCurrentPage('search')} className={`p-2 rounded-lg transition-colors ${currentPage === 'search' ? 'text-indigo-600 bg-indigo-50' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-4">
          <Search className="w-5 h-5" data-id="element-5" />
        </button>
        <button onClick={() => setCurrentPage('applicationHub')} className={`p-2 rounded-lg transition-colors ${currentPage === 'applicationHub' ? 'text-indigo-600 bg-indigo-50' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-6">
          <ClipboardList className="w-5 h-5" data-id="element-7" />
        </button>
        <div className="relative" data-id="element-8">
          {policyCreated && currentPage === 'policies' && <motion.div animate={{
          x: [0, 8, 0]
        }} transition={{
          duration: 1.2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute left-full ml-4 top-1/2 -translate-y-1/2 flex items-center gap-1.5 whitespace-nowrap" data-id="element-9">
              <ArrowLeft className="w-5 h-5 text-gray-900 -mr-1" data-id="element-10" />
              <span className="bg-gray-900 text-white text-sm font-medium px-3 py-1.5 rounded-lg shadow-lg" data-id="element-11">
                Go here
              </span>
            </motion.div>}
          <button onClick={() => setCurrentPage('application')} className={`p-2 rounded-lg transition-colors ${currentPage === 'application' ? 'text-indigo-600 bg-indigo-50' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-12">
            <FileText className="w-5 h-5" data-id="element-13" />
          </button>
        </div>
        <button onClick={() => setCurrentPage('policies')} className={`p-2 rounded-lg transition-colors ${currentPage === 'policies' ? 'text-indigo-600 bg-indigo-50' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-14">
          <Shield className={`w-5 h-5 ${currentPage === 'policies' ? 'fill-indigo-100' : ''}`} data-id="element-15" />
        </button>
        <button onClick={() => setCurrentPage('admin')} className={`p-2 rounded-lg transition-colors ${currentPage === 'admin' ? 'text-indigo-600 bg-indigo-50' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-16">
          <Bot className="w-5 h-5" data-id="element-17" />
        </button>
        <div className="flex-1" data-id="element-18" />
        <button className="text-gray-600 hover:text-gray-900" data-id="element-19">
          <Users className="w-5 h-5" data-id="element-20" />
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-21">
        {/* Top Navbar */}
        <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-4 z-10" data-id="element-22">
          <button className="text-gray-600 hover:text-gray-900" data-id="element-23">
            <Menu className="w-6 h-6" data-id="element-24" />
          </button>

          <img src="https://cdn.magicpatterns.com/uploads/qJVgA9PEfqwGLDv8KygeAy/Property_1Creit_Pulse_Logo_black_2_(16).png" alt="Credit Pulse" className="h-8 object-contain" data-id="element-25" />

          <GlobalSearch onAddComplete={() => setCurrentPage('application')} data-id="element-26" />

          <div className="flex items-center gap-3" data-id="element-27">
            <button className="w-10 h-10 rounded-full border-2 border-indigo-400 flex items-center justify-center text-indigo-600 hover:bg-indigo-50" data-id="element-28">
              <HelpCircle className="w-5 h-5" data-id="element-29" />
            </button>
            <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium" data-id="element-30">
              IN
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto" data-id="element-31">
          {currentPage === 'monitor' ? <MonitorPage onCompanyClick={company => handleCompanyClick(company as 'dunder' | 'wonka' | 'initech')} data-id="element-32" /> : currentPage === 'admin' ? <AdminDashboard data-id="element-33" /> : currentPage === 'search' ? <AdvancedSearch onResultClick={() => {
          setSelectedCompany('dunder');
          setCurrentPage('application');
        }} data-id="element-34" /> : currentPage === 'applicationHub' ? <ApplicationHub onViewApplication={() => {
          setSelectedCompany('dunder');
          setCurrentPage('application');
        }} data-id="element-35" /> : currentPage === 'application' ? selectedCompany === 'wonka' ? <WonkaCreditApplication data-id="element-36" /> : selectedCompany === 'initech' ? <InitechCreditApplication data-id="element-37" /> : <CreditApplication data-id="element-38" /> : currentPage === 'policies' ? <div className="px-8 py-6 max-w-7xl mx-auto" data-id="element-39">
              <div className="flex items-center justify-between mb-6" data-id="element-40">
                <div data-id="element-41">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-42">
                    Credit Policy Management
                  </h1>
                  <p className="text-gray-600" data-id="element-43">
                    Manage policies for New and Existing customers
                  </p>
                </div>
                <div className="relative" data-id="element-44">
                  {showStartArrow && !isModalOpen && <motion.div animate={{
                y: [0, -8, 0]
              }} transition={{
                duration: 1.2,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="absolute -bottom-14 right-0 flex items-center gap-1.5 whitespace-nowrap" data-id="element-45">
                      <span className="bg-gray-900 text-white text-sm font-medium px-3 py-1.5 rounded-lg shadow-lg" data-id="element-46">
                        Click here to start
                      </span>
                      <ArrowUpRight className="w-5 h-5 text-gray-900 -ml-1 -mt-4" data-id="element-47" />
                    </motion.div>}
                  <button onClick={handleOpenModal} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 font-medium relative z-10" data-id="element-48">
                    <span className="text-lg" data-id="element-49">+</span>
                    New Policy
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200 mb-6" data-id="element-50">
                <div className="flex gap-8" data-id="element-51">
                  <button className="pb-3 px-1 border-b-2 border-indigo-600 text-indigo-600 font-medium" data-id="element-52">
                    New Customer Policies
                  </button>
                  <button className="pb-3 px-1 text-gray-600 hover:text-gray-900 font-medium" data-id="element-53">
                    Existing Customer Policies
                  </button>
                </div>
              </div>

              {/* Search and Filter */}
              <div className="flex gap-4 mb-8" data-id="element-54">
                <div className="relative flex-1 max-w-sm" data-id="element-55">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-56" />
                  <input type="text" placeholder="Search policies" className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" data-id="element-57" />
                </div>
                <button className="px-4 py-2 border border-gray-300 rounded-lg flex items-center gap-2 text-gray-700 hover:bg-gray-50 font-medium" data-id="element-58">
                  <Filter className="w-4 h-4" data-id="element-59" />
                  Show Default Only
                </button>
              </div>

              {/* Policy Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-60">
                {/* Default Policy Card */}
                <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-61">
                  <div className="flex items-start justify-between mb-4" data-id="element-62">
                    <div data-id="element-63">
                      <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-64">
                        Credit Pulse Default Policy
                      </h3>
                      <div className="flex items-center gap-2 mb-3" data-id="element-65">
                        <span className="px-2 py-1 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded" data-id="element-66">
                          Default
                        </span>
                        <span className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded" data-id="element-67">
                          v1
                        </span>
                      </div>
                    </div>
                    <button className="text-gray-400 hover:text-gray-600" data-id="element-68">
                      <Copy className="w-5 h-5" data-id="element-69" />
                    </button>
                  </div>

                  <div className="space-y-1 mb-4 text-sm text-gray-600" data-id="element-70">
                    <p data-id="element-71">Type: New Customer</p>
                    <p data-id="element-72">
                      Last updated on Mar 27, 2026 by Integration Testing User
                    </p>
                  </div>

                  <button className="w-full bg-white border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50 py-2.5 rounded-lg font-medium" data-id="element-73">
                    Manage Policy &gt;
                  </button>
                </div>

                {/* New Policy Card */}
                <AnimatePresence data-id="element-74">
                  {policyCreated && <motion.div initial={{
                opacity: 0,
                scale: 0.9,
                y: 20
              }} animate={{
                opacity: 1,
                scale: 1,
                y: 0
              }} className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-75">
                      <div className="flex items-start justify-between mb-4" data-id="element-76">
                        <div data-id="element-77">
                          <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-78">
                            My Company's Credit Policy
                          </h3>
                          <div className="flex items-center gap-2 mb-3" data-id="element-79">
                            <span className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded" data-id="element-80">
                              v1
                            </span>
                          </div>
                        </div>
                        <button className="text-gray-400 hover:text-gray-600" data-id="element-81">
                          <Copy className="w-5 h-5" data-id="element-82" />
                        </button>
                      </div>

                      <div className="space-y-1 mb-4 text-sm text-gray-600" data-id="element-83">
                        <p data-id="element-84">Type: New Customer</p>
                        <p data-id="element-85">Last updated on Mar 29, 2026 by You</p>
                      </div>

                      <button className="w-full bg-white border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50 py-2.5 rounded-lg font-medium" data-id="element-86">
                        Manage Policy &gt;
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div> : <CreditApplication data-id="element-87" />}
        </main>
      </div>

      {/* Modal */}
      {isModalOpen && <CreatePolicyModal onClose={handleCloseModal} data-id="element-88" />}
    </div>;
}